import { AddFocusEvent, Format } from './format.interface';
import { Card, Query } from '@deckplan.io/common';
import { CommanderData } from '@deckplan.io/common/decks';
import { CardPickerComponent } from '../../cards/card-picker/card-picker.component';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export class Commander extends Format {

    private commander: Card;

    public addCardsQuery(): Query {
        const commanderData = this.deck.specialData as CommanderData;

        if (!commanderData || !commanderData.commanderId) {
            return null;
        }

        return {
            operator: 'and',
            operands: [
                {
                    dataField: {
                        title: 'Color Identity',
                        field: 'color_identity',
                        dataType: 'string'
                    },
                    operation: 'is in color identity',
                    value: commanderData.colorIdentity
                },
                {
                    operator: 'or',
                    operands: [
                        {
                            dataField: {
                                title: 'Name',
                                field: 'name',
                                dataType: 'string'
                            },
                            operation: 'singleton name',
                            value: this.deck.id
                        },
                        {
                            dataField: {
                                title: 'Type',
                                field: 'type_line',
                                dataType: 'string'
                            },
                            operation: 'startsWith',
                            value: 'Basic Land'
                        },
                        {
                            dataField: {
                                title: 'Text',
                                field: 'oracle_text',
                                dataType: 'string'
                            },
                            operation: 'includes',
                            value: 'any number of cards named'
                        }
                    ]
                },
                {
                    dataField: {
                        title: 'ID',
                        field: 'id',
                        dataType: 'string'
                    },
                    operation: '!=',
                    value: commanderData.commanderId
                },
                {
                    dataField: {
                        title: 'Legalities',
                        field: 'legalities',
                        dataType: 'string'
                    },
                    operation: 'includes',
                    value: `"commander":"legal"`
                }
            ]
        } as any;
    }

    public addFocused(): Observable<AddFocusEvent> {
        const typeDataField = {title: 'Type', field: 'type', dataType: 'string'};
        const commanderQuery = {
            operator: 'and',
            operands: [
                {
                    dataField: {title: 'Legalities', field: 'legalities', dataType: 'string'},
                    operation: 'includes',
                    value: `"commander":"legal"`
                },
                {
                    operator: 'or',
                    operands: [
                        {
                            dataField: typeDataField,
                            operation: 'includes',
                            value: 'Legendary%Creature'
                        },
                        {
                            operator: 'and',
                            operands: [
                                {
                                    dataField: typeDataField,
                                    operation: 'includes',
                                    value: 'Planeswalker'
                                },
                                {
                                    dataField: {title: 'Text', field: 'oracle_text', dataType: 'string'},
                                    operation: 'includes',
                                    value: 'can be your commander'
                                }
                            ]
                        }
                    ]
                }
            ]
        } as Query;
        const dialogRef = this.dialog.open(CardPickerComponent, {
            width: '100vw',
            minWidth: '100vw',
            height: '100vh',
            minHeight: '100vh',
            closeOnNavigation: false,
            disableClose: true,
            data: {
                query: commanderQuery,
                showOwnedCardsSelector: true,
                override: true
            }
        });
        return dialogRef.afterClosed().pipe(
            filter(card => card),
            map((card: any) => ({
                card,
                specialData: {
                    commanderId: card.id,
                    colorIdentity: card.colorIdentity
                } as CommanderData
            }))
        );
    }

    public deckCardsQuery(): Query {
        let query = null;
        const commanderData = this.deck.specialData as CommanderData;

        if (commanderData && commanderData.commanderId !== undefined) {
            query = {
                dataField: {field: 'id', dataType: 'string', title: 'ID'},
                operation: '!=',
                value: commanderData.commanderId
            };
        }

        return query;
    }

    public getFocused(): Observable<Card> {
        if (!this.deck.specialData) {
            return of(null);
        }

        const commanderData = this.deck.specialData as CommanderData;
        this.commander = this.deck.cards.find(card => card.id === commanderData.commanderId);

        if (commanderData && !this.commander && commanderData.commanderId) {
            return this.cardsService.getCard(commanderData.commanderId).pipe(map(card => this.commander = card));
        } else {
            return of(this.commander);
        }
    }

    public validate(): Card[] {
        let colorIdentity;
        if (!this.commander && !this.deck.specialData) {
            return this.deck.cards;
        } else if (!this.commander) {
            const commanderData = this.deck.specialData as CommanderData;
            colorIdentity = commanderData.colorIdentity;
        } else {
            colorIdentity = this.commander.colorIdentity;
        }

        return this.deck.cards.filter(c => {
            // All cards not legal in commander are invalid
            if (c.legalities.commander === 'not_legal' || !colorIdentity) {
                return true;
            }

            // All cards that have a color not in the commanders
            // color identity are invalid
            for (const ci of c.colorIdentity) {
                if (!colorIdentity.find(i => i === ci)) {
                    return true;
                }
            }

            // All cards that are not basic lands or say you can have more (i.e. Relentless rats)
            // and are duplicated in the deck are invalid
            if (c.type && !c.type.includes('Basic Land') && !c.oracleText.includes(`any number of cards named ${c.name}`)) {
                return c.quantityInDeck > 1 || this.deck.cards.filter(innerCard => innerCard.name === c.name).length > 1;
            }

            return false;
        });
    }

    public selectFocusText(): string {
        return 'Select Commander';
    }

}
